import { AxiosInstance } from 'axios';
import { API_BASE_URL } from '../../util/constants';

/** Which document family a batch belongs to; invoices and their delivery notes are "sale". */
export type DocImageKind = 'offer' | 'sale';

/** Where a line's current image comes from. EDITED is local: changed in this review. */
export type LineImageOrigin = 'SAVED' | 'OFFER' | 'CATALOG' | 'NONE' | 'EDITED';

export type HistorySource = 'CATALOG' | 'INVOICE' | 'OFFER';

export interface LineImage {
	item: number;
	amount: number;
	item_desc: string;
	/** Stored image hash; null for a catalog photo not yet frozen, or no image. */
	image: string | null;
	/** Relative to the API base. */
	url: string | null;
	origin: LineImageOrigin;
}

export interface HistoryImage {
	source: HistorySource;
	image: string;
	url: string;
	last_used: string;
}

export const docImageSrc = (url: string | null | undefined) => (url ? `${API_BASE_URL}${url}` : '');

export const fetchLineImages = (api: AxiosInstance, kind: DocImageKind, batch: string) =>
	api.get<{ saved: boolean; lines: LineImage[] }>(`documents/${kind}/${encodeURIComponent(batch)}/line-images`);

/**
 * Freeze every line: a line still on its catalog photo asks the server to copy
 * that photo, so later catalog changes leave this document alone.
 */
export const saveLineImages = (api: AxiosInstance, kind: DocImageKind, batch: string, lines: LineImage[]) =>
	api.put(`documents/${kind}/${encodeURIComponent(batch)}/line-images`, {
		lines: lines.map(({ item, amount, item_desc, image, origin }) =>
			origin === 'CATALOG'
				? { item, amount, item_desc, image: null, catalog_role: 'main' }
				: { item, amount, item_desc, image: origin === 'NONE' ? null : image },
		),
	});

export const uploadDocImage = (api: AxiosInstance, blob: Blob) => {
	const form = new FormData();
	form.append('image', blob, 'line.jpg');
	return api.post<{ image: string; url: string }>('documents/images', form, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});
};

export const fetchImageHistory = (
	api: AxiosInstance,
	itemId: number,
	prefer: DocImageKind,
	page: number,
	perPage: number,
) =>
	api.get<{ total: number; rows: HistoryImage[] }>(`items/${itemId}/image-history`, {
		params: { prefer, page, per_page: perPage },
	});

/** Load a remote image as a local object URL, so the crop canvas is never cross-origin tainted. */
export const loadAsObjectUrl = async (src: string): Promise<string> => {
	const res = await fetch(src, { mode: 'cors' });
	if (!res.ok) throw new Error(`image ${res.status}`);
	return URL.createObjectURL(await res.blob());
};
